import { z } from 'zod'
import { amGeometryMultiPolygonSchema, amGeometryPolygonSchema } from 'src/common-services/types/alphamap/geojson-types'
import { amLocationShema } from 'src/common-services/types/alphamap/locations-types'
import { pointSchema } from 'src/common-services/types/util-types'
import { amDateShema } from 'src/common-services/types/alphamap/generic-types'

export const parcelPointFeatureSchema = z.object({
  type: z.literal('Feature'),
  properties: z.object({
    parcelId: z.string(),
    address: z.string().optional().nullable(),
    apn: z.string(),
  }),
  geometry: pointSchema,
})
export type AMParcelPointFeature = z.infer<typeof parcelPointFeatureSchema>

export const parcelPolygonFeatureSchema = z.object({
  type: z.literal('Feature'),
  properties: z.object({}),
  geometry: amGeometryPolygonSchema.or(amGeometryMultiPolygonSchema),
})
export type AMParcelPolygonFeature = z.infer<typeof parcelPolygonFeatureSchema>

const amOwnershipOwnerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string(),
})

export const amOwnershipParcelSchema = z.object({
  id: z.string().uuid(),
  parents: z.array(z.string().uuid()).optional(),
  address: z.string().optional().nullable(),
  apn: z.string().optional(),
})

export type AMOwnershipParcel = z.infer<typeof amOwnershipParcelSchema>

export const amOwnershipParcelCompleteSchema = amOwnershipParcelSchema.extend({
  mCode: z.string().nullable(),
  propertyType: z.string().nullable(),
  geometry: amGeometryPolygonSchema.or(amGeometryMultiPolygonSchema),
  owners: z.array(amOwnershipOwnerSchema),
  brandedLocations: z.array(amLocationShema),
  point: parcelPointFeatureSchema,
  city: z.string().nullish(),
  zip: z.string().nullish(),
  state: z.string().nullish(),
  taxAssessorId: z.string(),
})

export type AMOwnershipParcelComplete = z.infer<typeof amOwnershipParcelCompleteSchema>

// Simple brand schema for parcel preview endpoint
const amOwnershipParcelTenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})
export type AMOwnershipParcelTenant = z.infer<typeof amOwnershipParcelTenantSchema>

const amOwnershipParcelPreviewSchema = amOwnershipParcelSchema.extend({
  tenants: z.array(amOwnershipParcelTenantSchema),
})
export type AMOwnershipParcelPreview = z.infer<typeof amOwnershipParcelPreviewSchema>

export const amOwnershipEntitySearchResult = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  type: z.string(),
  amountOfContacts: z.number(),
  amountOfParcels: z.number(),
  parcelsTypesDistribution: z.array(
    z.object({
      mCode: z.string(),
      count: z.number(),
    })
  ),
})
export type AMOwnershipEntitySearchResult = z.infer<typeof amOwnershipEntitySearchResult>

// Routes request and response schemas
// Search suggestions for both entities and parcels
export const amOwnershipSearchSuggestionsRequestSchema = z
  .object({
    address: z.string(),
    type: z.literal('PLACE'),
  })
  .or(
    z.object({
      name: z.string().optional(),
      address: z.string().optional(),
      type: z.literal('OWNER'),
    })
  )
export type AMOwnershipSearchSuggestionsRequest = z.infer<typeof amOwnershipSearchSuggestionsRequestSchema>

const amOwnershipSearchSuggestionSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  subtitle: z.string().optional(),
  type: z.enum(['OWNER', 'PLACE']),
})
export type AMOwnershipSearchSuggestion = z.infer<typeof amOwnershipSearchSuggestionSchema>

const amOwnershipSearchSuggestionsResponseSchema = z.array(amOwnershipSearchSuggestionSchema)
export type AMOwnershipSearchSuggestionsResponse = z.infer<typeof amOwnershipSearchSuggestionsResponseSchema>

export const amOwnershipGetEntitiesByIdsRequestSchema = z.array(z.string().uuid())
export type AMOwnershipGetEntitiesByIdsRequest = z.infer<typeof amOwnershipGetEntitiesByIdsRequestSchema>

// Get parcel by id
export const amOwnershipGetParcelByIdRequestSchema = z.string().uuid()
export type AMOwnershipGetParcelByIdRequest = z.infer<typeof amOwnershipGetParcelByIdRequestSchema>

export const amOwnershipGetParcelByIdResponseSchema = amOwnershipParcelCompleteSchema.optional()
export type AMOwnershipGetParcelByIdResponse = z.infer<typeof amOwnershipGetParcelByIdResponseSchema>

// Parcel preview
export const amOwnershipGetParcelPreviewRequestSchema = z.string().uuid()
export type AMOwnershipGetParcelPreviewRequest = z.infer<typeof amOwnershipGetParcelPreviewRequestSchema>

export const amOwnershipGetParcelPreviewResponseSchema = amOwnershipParcelPreviewSchema.optional()
export type AMOwnershipGetParcelPreviewResponse = z.infer<typeof amOwnershipGetParcelPreviewResponseSchema>

export const amOwnershipGetParcelGraphVisualRequestSchema = z.string().uuid()
export type AMOwnershipGetParcelGraphVisualRequest = z.infer<typeof amOwnershipGetParcelGraphVisualRequestSchema>

export const amOwnershipGetParcelGraphContactsRequestSchema = z.string().uuid()
export type AMOwnershipGetParcelGraphContactsRequest = z.infer<typeof amOwnershipGetParcelGraphContactsRequestSchema>

export const amOwnershipGraphVisualRelationSchema = z.object({
  from: z.string().uuid(),
  to: z.string().uuid(),
  isDirect: z.boolean(),
})
export type AMOwnershipGraphVisualRelation = z.infer<typeof amOwnershipGraphVisualRelationSchema>

const amOwnershipElementTypeSchema = z.enum(['ORGANIZATION', 'PERSON', 'UNKNOWN', 'PARCEL'])
export type AMOwnershipElementType = z.infer<typeof amOwnershipElementTypeSchema>

export const amOwnershipGraphVisualNodeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: amOwnershipElementTypeSchema,
  isRoot: z.boolean(),
  isDirect: z.boolean().optional(),
  relationsCount: z.number(),
  layer: z.number().optional(),
})
export type AMOwnershipGraphVisualNode = z.infer<typeof amOwnershipGraphVisualNodeSchema>

// Search filters
const propertyFiltersSchema = z.object({
  lotSize: z.tuple([z.number(), z.number()]),
  lotType: z.enum(['acres', 'sqFt']),
  mCodes: z.array(z.string()),
  numberOfBuildings: z.tuple([z.number(), z.number()]),
  numberOfCommercialUnits: z.tuple([z.number(), z.number()]),
  numberOfStories: z.tuple([z.number(), z.number()]),
  yearBuilt: z.tuple([z.number(), z.number()]),
})
export type AMOwnershipPropertyFilters = z.infer<typeof propertyFiltersSchema>

const financesFiltersSchema = z.object({
  lenderName: z.string(),
  lenderNameFilterType: z.enum(['includes', 'excludes']),
  mortgageAmount: z.tuple([z.number(), z.number()]),
  multiParcelSale: z.boolean(),
  saleDate: amDateShema.optional(),
  salePrice: z.tuple([z.number().optional(), z.number().optional()]),
  taxAmount: z.tuple([z.number().optional(), z.number().optional()]),
  yearOverYearChange: z.tuple([z.number().optional(), z.number().optional()]),
})
export type AMOwnershipFinancesFilters = z.infer<typeof financesFiltersSchema>

const ownerFiltersSchema = z.object({
  activityStatus: z.enum(['active', 'inactive']).optional(),
  hasContantInformation: z.boolean(),
  numberOfOwnedParcels: z.tuple([z.number(), z.number()]),
  ownerType: z.enum(['organization', 'individual']).optional(),
  portfolioValue: z.tuple([z.number(), z.number()]),
})
export type AMOwnershipOwnerFilters = z.infer<typeof ownerFiltersSchema>

const allOwnershipFilters = propertyFiltersSchema.merge(financesFiltersSchema).merge(ownerFiltersSchema)
const amOwnershipSearchFiltersSchema = allOwnershipFilters.partial()
export type AMOwnershipSearchFilters = z.infer<typeof amOwnershipSearchFiltersSchema>

// Get Parcel ID by point
export const amOwnershipSearchParcelsByPointRequestSchema = pointSchema
export type AMOwnershipSearchParcelsByPointRequest = z.infer<typeof amOwnershipSearchParcelsByPointRequestSchema>

export const amOwnershipSearchParcelsByFeaturesRequestSchema = z.object({
  features: z.array(amGeometryPolygonSchema),
  page: z.number(),
  lotSize: z.array(z.number()).optional(),
  lotType: z.enum(['acres', 'sqFt']).optional(),
  yearBuilt: z.array(z.number()).optional(),
  numberOfStories: z.array(z.number()).optional(),
  numberOfBuildings: z.array(z.number()).optional(),
  numberOfCommercialUnits: z.array(z.number()).optional(),
  mCodes: z.array(z.string()).optional(),
})

export type AMOwnershipSearchParcelsByFeaturesRequest = z.infer<typeof amOwnershipSearchParcelsByFeaturesRequestSchema>

const amOwnershipEntityOverviewResponseSchema = z.object({
  id: z.string().uuid(),
  companyType: z.string(),
  address: z.string(),
  incorporationDate: z.string(),
  activity: z.boolean(),
  lastActivity: z.string(),
  amountOfRelatedOwners: z.number(),
  amountOfContacts: z.number(),
  amountOfParcels: z.number(),
  parcelsValue: z.number(),
  parcelPoints: z.array(parcelPointFeatureSchema),
  parcelPolygons: z.array(parcelPolygonFeatureSchema),
})
export type AMOwnershipEntityOverviewResponse = z.infer<typeof amOwnershipEntityOverviewResponseSchema>

export const amOwnershipEntityPreviewResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string(),
  type: z.string(),
  altPosition: z.string().array(),
  companyType: z.string().nullable(),
  amountOfContacts: z.number(),
  amountOfParcels: z.number(),
  parcelsSqFt: z.number(),
  parcelsValue: z.number(),
  parcelsTypesDistribution: z.array(
    z.object({
      mCode: z.string(),
      count: z.number(),
    })
  ),
  parcelsStateDistribution: z.record(z.string(), z.number()),
})
export type AMOwnershipEntityPreviewResponse = z.infer<typeof amOwnershipEntityPreviewResponseSchema>

export const amOwnershipSearchEntitiesRequestSchema = z.object({
  name: z.string(),
  address: z.string().optional(),
  page: z.number(),
  filters: amOwnershipSearchFiltersSchema,
  type: z.enum(['PERSONS', 'ORGANIZATIONS']),
})
export type AMOwnershipSearchEntitiesRequest = z.infer<typeof amOwnershipSearchEntitiesRequestSchema>

export const amOwnershipSearchEntitiesResponseSchema = z.array(amOwnershipEntitySearchResult)
export type AMOwnershipSearchEntitiesResponse = z.infer<typeof amOwnershipSearchEntitiesResponseSchema>
export enum amOwnershipEntityTypeEnum {
  PERSON = 'PERSON',
  ORGANIZATION = 'ORGANIZATION',
  UNKNOWN = 'UNKNOWN',
}

export const amOwnershipEntityTypeShema = z.nativeEnum(amOwnershipEntityTypeEnum)

export type AMOwnershipEntityType = z.infer<typeof amOwnershipEntityTypeShema>

export const amOwnershipEntityParentEntitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: amOwnershipEntityTypeShema,
})

export type AMOwnershipEntityParent = z.infer<typeof amOwnershipEntityParentEntitySchema>

export const amOwnershipEntityParentsEntitySchema = z.array(amOwnershipEntityParentEntitySchema)

export const amOwnershipEntitySchema = z.object({
  id: z.string().uuid(),
  parents: z.array(z.string().uuid()).optional(),
  name: z.string().optional(),
  address: z.string().optional().nullable(),
  type: amOwnershipEntityTypeShema,
  position: z.string().nullable(),
})

export type AMOwnershipEntity = z.infer<typeof amOwnershipEntitySchema>

export const amOwnershipGraphEntitySchema = amOwnershipEntitySchema.extend({
  isDirect: z.boolean(),
})
export type AMOwnershipGraphEntity = z.infer<typeof amOwnershipGraphEntitySchema>

const amOwnershipEntityGraphOverviewSchema = z.object({
  id: z.string().uuid(),
  inferredContacts: z.number(),
  relatedOrganizations: z.number(),
  relatedContacts: z.number(),
  relatedParcels: z.number(),
})

export type AMOwnershipEntityGraphOverview = z.infer<typeof amOwnershipEntityGraphOverviewSchema>

export const amOwnershipGetEntityGraphOverviewRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityGraphOverviewRequest = z.infer<typeof amOwnershipGetEntityGraphOverviewRequestSchema>

export const amOwnershipGetEntityAcquisitionOverviewRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityAcquisitionOverviewRequest = z.infer<
  typeof amOwnershipGetEntityAcquisitionOverviewRequestSchema
>

export const amOwnershipEntityAcquisitionOverviewSchema = z.object({
  id: z.string().uuid(),
  amountOfContacts: z.number().nullable().optional(),
  companyType: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  activity: z.string().nullable().optional(),
  portfolioValue: z.number().nullable().optional(),
  incorporationDate: z.string().nullable().optional(),
  // We dont have this as yet
  recentAcquisition: z.string().nullable().optional(),
  lastActivity: z.string().nullable().optional(),
})
export type AMOwnershipEntityAcquisitionOverview = z.infer<typeof amOwnershipEntityAcquisitionOverviewSchema>
const amOwnershipEntityGraphContactSchema = amOwnershipEntitySchema.extend({
  parentsObjects: amOwnershipEntityParentsEntitySchema,
  infoAvailable: z.boolean(),
})
export type AMOwnershipEntityGraphContact = z.infer<typeof amOwnershipEntityGraphContactSchema>

const amOwnershipEntityContactsAvailabilitySchema = z.object({
  workEmail: z.boolean(),
  mobilePhone: z.boolean(),
  phoneNumbers: z.boolean(),
  emails: z.boolean(),
})
export type AMOwnershipEntityContactsAvailability = z.infer<typeof amOwnershipEntityContactsAvailabilitySchema>

const amOwnershipEntityGraphRelatedEntitySchema = amOwnershipEntitySchema.extend({
  parentsObjects: amOwnershipEntityParentsEntitySchema,
})
export type AMOwnershipEntityGraphRelatedEntity = z.infer<typeof amOwnershipEntityGraphRelatedEntitySchema>

export const amOwnershipGetEntityGraphContactsRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityGraphContactsRequest = z.infer<typeof amOwnershipGetEntityGraphContactsRequestSchema>

export const amOwnershipGetEntityGraphParcelsRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityGraphParcelsRequest = z.infer<typeof amOwnershipGetEntityGraphParcelsRequestSchema>

export const amOwnershipGetEntityPropertyTypeBreakdownRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityPropertyTypeBreakdownRequest = z.infer<
  typeof amOwnershipGetEntityPropertyTypeBreakdownRequestSchema
>

const amOwnershipEntityGraphParcelSchema = amOwnershipParcelSchema.extend({
  parentsObjects: amOwnershipEntityParentsEntitySchema,
  lotSizeSqft: z.number().nullable(),
  lotSizeAcre: z.number().nullable(),
  numberOfBuildings: z.number().nullable(),
  commercialUnits: z.number().nullable(),
  stories: z.number().nullable(),
  mCode: z.string(),
  yearBuilt: z.number().nullable(),
  previousSaleAmount: z.number().nullable(),
  previousSaleDate: z.date().nullable(),
  totalAssessedValue: z.number().nullable(),
})

export const amOwnershipEntityGraphParcelDBSchema = amOwnershipEntityGraphParcelSchema.omit({
  parentsObjects: true,
})

export type AMOwnershipEntityGraphParcel = z.infer<typeof amOwnershipEntityGraphParcelSchema>

export const amOwnershipEntityPropertyTypeBreakdownSchema = z.record(
  z.string(),
  z.object({
    count: z.number(),
    value: z.number(),
    sqft: z.number(),
  })
)
export type AMOwnershipEntityPropertyTypeBreakdown = z.infer<typeof amOwnershipEntityPropertyTypeBreakdownSchema>

export const amOwnershipGetEntityGraphVisualRequestSchema = z.string().uuid()
export type AMOwnershipGetEntityGraphVisualRequest = z.infer<typeof amOwnershipGetEntityGraphVisualRequestSchema>

export const amOwnershipGraphVisualResponseSchema = z.object({
  nodes: z.array(amOwnershipGraphVisualNodeSchema),
  relations: z.array(amOwnershipGraphVisualRelationSchema),
})

export type AMOwnershipGraphVisualResponse = z.infer<typeof amOwnershipGraphVisualResponseSchema>

export const parcelOverviewSchema = z.object({
  id: z.string().uuid(),
  address: z.string(),
  state: z.string(),
  city: z.string(),
  zip: z.string(),
  mCode: z.string(),
  apn: z.string(),
  lotSizeSqft: z.number().nullable(),
  lotSizeAcre: z.number().nullable(),
  numberOfBuildings: z.number().nullable(),
  commercialUnits: z.number().nullable(),
  stories: z.number().nullable(),
  yearBuilt: z.number().nullable(),
  previousSaleAmount: z.number().nullable(),
  previousSaleDate: z.date().nullable(),
  totalAssessedValue: z.number().nullable(),
  geometry: pointSchema,
  owners: z.array(amOwnershipOwnerSchema),
})
export type AMParcelOverview = z.infer<typeof parcelOverviewSchema>
export const amOwnershipEntityCluster90PlusVectors = z.object({
  am_entity_cluster_90_id: z.string().uuid(),
  am_entity_cluster_90_plus_id: z.string().uuid().nullable(),
  am_entity_type: z.string(),
  am_name_vector_person: z.string(),
  am_name_vector_organization: z.string(),
})
export type AMOwnershipEntityCluster90PlusVectors = z.infer<typeof amOwnershipEntityCluster90PlusVectors>

export const amOwnershipGetEntityGraphRewritesVisualResponseSchema = z.object({
  from: z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
  }),
  to: z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
  }),
})

export const amOwnershipL5EntitySchema = z.object({
  name: z.string(),
  address: z.string().optional(),
  providerEntityId: z.string(),
  providerId: z.string(),
})
export type AMOwnershipL5Entity = z.infer<typeof amOwnershipL5EntitySchema>

export const amOwnershipRootEntitySchema = z.object({
  accounts_last_made_up_date: z.string().nullable(),
  accounts_next_due: z.string().nullable(),
  accounts_reference_date: z.string().nullable(),
  address_country: z.string().nullable(),
  address_in_full: z.string().nullable(),
  address_locality: z.string().nullable(),
  address_postal_code: z.string().nullable(),
  address_region: z.string().nullable(),
  address_street_address: z.string().nullable(),
  am_address: z.string().nullable(),
  am_entity_cluster_l0_id: z.string().nullable(),
  am_entity_cluster_l1_address_score: z.string().nullable(),
  am_entity_cluster_l1_id: z.string().nullable(),
  am_entity_cluster_l1_name_score: z.string().nullable(),
  am_entity_cluster_l1_seed: z.string().nullable(),
  am_entity_cluster_l2_id: z.string().nullable(),
  am_entity_cluster_l3_id: z.string().nullable(),
  am_entity_cluster_l4_id: z.string().nullable(),
  am_entity_cluster_l5_id: z.string().nullable(),
  am_entity_type: z.string().nullable(),
  am_name: z.string().nullable(),
  am_postal_category: z.string().nullable(),
  am_postal_city: z.string().nullable(),
  am_postal_city_district: z.string().nullable(),
  am_postal_country: z.string().nullable(),
  am_postal_country_region: z.string().nullable(),
  am_postal_entrance: z.string().nullable(),
  am_postal_house: z.string().nullable(),
  am_postal_house_number: z.string().nullable(),
  am_postal_island: z.string().nullable(),
  am_postal_level: z.string().nullable(),
  am_postal_near: z.string().nullable(),
  am_postal_po_box: z.string().nullable(),
  am_postal_postcode: z.string().nullable(),
  am_postal_postcode_5: z.string().nullable(),
  am_postal_road: z.string().nullable(),
  am_postal_staircase: z.string().nullable(),
  am_postal_state: z.string().nullable(),
  am_postal_state_district: z.string().nullable(),
  am_postal_suburb: z.string().nullable(),
  am_postal_unit: z.string().nullable(),
  am_postal_world_region: z.string().nullable(),
  am_provider_entity_id: z.string().nullable(),
  am_provider_entity_parent_id: z.string().nullable(),
  am_provider_id: z.number().nullable(),
  am_provider_parcel_id: z.string().nullable(),
  am_rel_entity: z.boolean().nullable(),
  am_unique_provider_entity_id: z.string().nullable(),
  annual_return_last_made_up_date: z.string().nullable(),
  annual_return_next_due: z.string().nullable(),
  branch: z.string().nullable(),
  business_number: z.string().nullable(),
  cherre_address__address: z.string().nullable(),
  cherre_address__mailing_address: z.string().nullable(),
  cherre_address__property_address: z.string().nullable(),
  cherre_ingest_datetime: z.string().nullable(),
  cherre_recorder_grantee_pk: z.string().nullable(),
  cherre_recorder_grantor_pk: z.string().nullable(),
  cherre_tax_assessor_owner_pk: z.string().nullable(),
  cherre_usa_owner_unmask_pk: z.string().nullable(),
  company_number: z.string().nullable(),
  company_type: z.string().nullable(),
  country_of_residence: z.string().nullable(),
  current_alternative_legal_name: z.string().nullable(),
  current_alternative_legal_name_language: z.string().nullable(),
  current_status: z.string().nullable(),
  dissolution_date: z.string().nullable(),
  end_date: z.string().nullable(),
  first_name: z.string().nullable(),
  grantee_address: z.string().nullable(),
  grantee_care_of_name: z.string().nullable(),
  grantee_city: z.string().nullable(),
  grantee_crrt: z.string().nullable(),
  grantee_entity_code: z.string().nullable(),
  grantee_entity_count: z.string().nullable(),
  grantee_first_name: z.string().nullable(),
  grantee_house_number: z.string().nullable(),
  grantee_last_name: z.string().nullable(),
  grantee_middle_name: z.string().nullable(),
  grantee_name: z.string().nullable(),
  grantee_name_suffix: z.string().nullable(),
  grantee_owner_type: z.string().nullable(),
  grantee_state: z.string().nullable(),
  grantee_street_direction: z.string().nullable(),
  grantee_street_name: z.string().nullable(),
  grantee_street_post_direction: z.string().nullable(),
  grantee_street_suffix: z.string().nullable(),
  grantee_unit_number: z.string().nullable(),
  grantee_unit_prefix: z.string().nullable(),
  grantee_vesting_deed_code: z.string().nullable(),
  grantee_vesting_recorded_code: z.string().nullable(),
  grantee_zip: z.string().nullable(),
  grantee_zip_4: z.string().nullable(),
  grantor_address: z.string().nullable(),
  grantor_city: z.string().nullable(),
  grantor_crrt: z.string().nullable(),
  grantor_entity_code: z.string().nullable(),
  grantor_first_name: z.string().nullable(),
  grantor_house_number: z.string().nullable(),
  grantor_last_name: z.string().nullable(),
  grantor_middle_name: z.string().nullable(),
  grantor_name: z.string().nullable(),
  grantor_name_suffix: z.string().nullable(),
  grantor_state: z.string().nullable(),
  grantor_street_direction: z.string().nullable(),
  grantor_street_name: z.string().nullable(),
  grantor_street_post_direction: z.string().nullable(),
  grantor_street_suffix: z.string().nullable(),
  grantor_unit_number: z.string().nullable(),
  grantor_unit_prefix: z.string().nullable(),
  grantor_zip: z.string().nullable(),
  grantor_zip_4: z.string().nullable(),
  has_been_liquidated: z.string().nullable(),
  has_charges: z.string().nullable(),
  has_confidence: z.string().nullable(),
  has_insolvency_history: z.string().nullable(),
  home_jurisdiction_code: z.string().nullable(),
  home_jurisdiction_company_number: z.string().nullable(),
  home_jurisdiction_text: z.string().nullable(),
  id: z.string().nullable(),
  inactive: z.string().nullable(),
  incorporation_date: z.string().nullable(),
  industry_code_uids: z.string().nullable(),
  is_owner_company: z.string().nullable(),
  jurisdiction_code: z.string().nullable(),
  last_name: z.string().nullable(),
  last_seen_date: z.string().nullable(),
  latest_accounts_assets: z.string().nullable(),
  latest_accounts_cash: z.string().nullable(),
  latest_accounts_date: z.string().nullable(),
  latest_accounts_liabilities: z.string().nullable(),
  name: z.string().nullable(),
  nationality: z.string().nullable(),
  native_company_number: z.string().nullable(),
  nonprofit: z.string().nullable(),
  normalised_name: z.string().nullable(),
  number_of_employees: z.string().nullable(),
  occupation: z.string().nullable(),
  occurrences_count: z.string().nullable(),
  owner_first_name: z.string().nullable(),
  owner_id: z.string().nullable(),
  owner_last_name: z.string().nullable(),
  owner_middle_name: z.string().nullable(),
  owner_name: z.string().nullable(),
  owner_name_latest_deed: z.string().nullable(),
  owner_name_suffix: z.string().nullable(),
  owner_state: z.string().nullable(),
  owner_trust_type_code: z.string().nullable(),
  owner_type: z.string().nullable(),
  ownership_vesting_relation_code: z.string().nullable(),
  partial_date_of_birth: z.string().nullable(),
  person_number: z.string().nullable(),
  person_uid: z.string().nullable(),
  position: z.string().nullable(),
  previous_names: z.string().nullable(),
  recorder_id: z.string().nullable(),
  registered_address_country: z.string().nullable(),
  registered_address_in_full: z.string().nullable(),
  registered_address_locality: z.string().nullable(),
  registered_address_postal_code: z.string().nullable(),
  registered_address_region: z.string().nullable(),
  registered_address_street_address: z.string().nullable(),
  registry_url: z.string().nullable(),
  restricted_for_marketing: z.string().nullable(),
  retrieved_at: z.string().nullable(),
  source_url: z.string().nullable(),
  start_date: z.string().nullable(),
  tax_assessor_id: z.string().nullable(),
  title: z.string().nullable(),
  type: z.string().nullable(),
})
export type AMOwnershipRootEntity = z.infer<typeof amOwnershipRootEntitySchema>
